import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../../../../config/api';
import GlobalHeader from '../../../../components/GlobalHeader.jsx';
import EditableCustomerEventCard from '../../EventHost/components/EditableCustomerEventCard.jsx';
import { fixS3ImageUrl } from '../../../../components/imageUtils';

import '../css/HostRequest.css';

const ViewEventRequest = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(0);
  const [selectedEvent, setSelectedEvent] = useState(location.state?.event || null);

  // Fetch full event details from the organiser API using the
  // normalised schema, so we get all fields (including packages,
  // tabs, cover image and background) directly from the DB.
  useEffect(() => {
    const initialEvent = location.state?.event || null;
    const eventId = initialEvent?.eventid || initialEvent?.event_id || initialEvent?.id;

    if (!eventId) return;

    const fetchEvent = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/');
          return;
        }

        console.log('[ViewEventRequest] 🔍 Fetching — API: GET /organiser/events/:eventId', { eventId });
        const response = await fetch(`${API_BASE_URL}/organiser/events/${eventId}`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });

        if (!response.ok) {
          console.error('Failed to fetch event details for view:', response.status, response.statusText);
          return;
        }

        const data = await response.json();
        console.log('[ViewEventRequest] ✅ Event details loaded:', {
          columns: 'event_name/name, description, start_date/date, end_date, start_time, end_time, location, event_type/type, capacity/total_tickets_planned, terms_and_conditions, cover_image_url/file_url, background_images_array/background, packages[{name,price,max_tickets,duration,start_sale,end_sale,description}], tab_sections[{name,content}]',
          eventId,
          data,
        });
        setSelectedEvent(data);
      } catch (err) {
        console.error('Error fetching event for view:', err);
      }
    };

    fetchEvent();
  }, [location.state, navigate]);

  const {
    formData,
    packages,
    tabs,
    placeholderCoverUrl,
    backgroundMode,
    imageBgColor,
    imageBgColor2,
    bgImageUrl,
    eventTypeOptions,
  } = useMemo(() => {
    if (!selectedEvent) {
      return {
        formData: {},
        packages: [],
        tabs: [],
        placeholderCoverUrl: '/fallback-logo.png',
        backgroundMode: 'solid',
        imageBgColor: '#111827',
        imageBgColor2: '#1f2937',
        bgImageUrl: '',
        eventTypeOptions: [],
      };
    }

    const toHtmlDate = (value) => {
      if (!value) return '';
      if (typeof value === 'string') {
        // If it's an ISO string like 2026-01-31T22:00:00.000Z, take the date part
        if (value.length >= 10) return value.slice(0, 10);
        return value;
      }
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return '';
      return d.toISOString().slice(0, 10);
    };

    const toHtmlTime = (value) => {
      if (!value) return '';
      if (typeof value === 'string') {
        // If it's an ISO string, extract HH:MM
        if (value.includes('T')) return value.split('T')[1].slice(0, 5);
        if (value.length >= 5) return value.slice(0, 5);
        return value;
      }
      const d = new Date(`1970-01-01T${value}`);
      if (Number.isNaN(d.getTime())) return '';
      return d.toTimeString().slice(0, 5);
    };

    const mappedFormData = {
      name: selectedEvent.event_name || selectedEvent.name || '',
      description: selectedEvent.description || '',
      startdate: toHtmlDate(selectedEvent.start_date || selectedEvent.date || ''),
      enddate: toHtmlDate(selectedEvent.end_date || selectedEvent.enddate || ''),
      start_time: toHtmlTime(selectedEvent.start_time || selectedEvent.time || ''),
      end_time: toHtmlTime(selectedEvent.end_time || selectedEvent.endtime || ''),
      location: selectedEvent.location || '',
      type: selectedEvent.event_type || selectedEvent.type || '',
      capacity: selectedEvent.capacity || selectedEvent.total_tickets_planned || '',
      // Additional fields (may not be present but kept for completeness)
      terms_and_conditions: selectedEvent.terms_and_conditions || '',
      organizer: selectedEvent.organizer || '',
      category: selectedEvent.category || '',
      status: selectedEvent.status || 'Pending Approval',
      contact_email: selectedEvent.contact_email || '',
      contact_phone: selectedEvent.contact_phone || '',
    };

    const mappedPackages = (selectedEvent.packages || []).map((pkg) => ({
      packageType: pkg.name || '',
      numberOfTickets: pkg.max_tickets ?? '',
      duration: pkg.duration ?? '',
      startDate: pkg.start_sale || '',
      endDate: pkg.end_sale || '',
      pricing: pkg.price != null ? String(pkg.price) : '',
      details: pkg.description || '',
      validFromDate: pkg.valid_from_date || '',
      validToDate: pkg.valid_to_date || '',
      validFromTime: pkg.valid_from_time || '',
      validToTime: pkg.valid_to_time || '',
    }));

    const mappedTabs = (selectedEvent.tab_sections || []).map((tab) => ({
      name: tab.name || '',
      content: tab.content || '',
    }));

    // Derive background settings from the stored background array
    let bgArray = selectedEvent.background_images_array || selectedEvent.background || null;
    let derivedMode = 'solid';
    let color1 = '#111827';
    let color2 = '#1f2937';
    let imageUrl = '';

    // Allow background to be either a real array, JSON-stringified array,
    // or a Postgres text[] string like {image,"https://..."}
    if (!Array.isArray(bgArray) && typeof bgArray === 'string') {
      const trimmed = bgArray.trim();

      // First, try JSON.parse (e.g. '["image","https://..."]')
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          bgArray = parsed;
        }
      } catch {
        // If it's not JSON, handle Postgres array syntax
        if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
          const inner = trimmed.slice(1, -1);
          const parts = inner.split(',');
          if (parts.length > 0) {
            bgArray = parts.map((p) => {
              const t = p.trim();
              if ((t.startsWith('"') && t.endsWith('"')) || (t.startsWith("'") && t.endsWith("'"))) {
                return t.slice(1, -1);
              }
              return t;
            });
          }
        }
      }
    }

    if (Array.isArray(bgArray) && bgArray.length > 0) {
      const mode = bgArray[0];
      const value1 = bgArray[1]; // second column in the Background array (URL when mode === 'image')
      const value2 = bgArray[2];

      if (mode === 'solid' && value1) {
        derivedMode = 'solid';
        color1 = value1;
      } else if (mode === 'gradient' && value1 && value2) {
        derivedMode = 'gradient';
        color1 = value1;
        color2 = value2;
      } else if (mode === 'image' && value1) {
        derivedMode = 'image';
        // Process S3 background URL through the same fixer/proxy logic
        // used by EventImage so it loads correctly as a background.
        imageUrl = fixS3ImageUrl(value1);
      }
    }

    const currentType = mappedFormData.type;
    const eventTypeOptions = currentType ? [currentType] : [];

    return {
      formData: mappedFormData,
      packages: mappedPackages,
      tabs: mappedTabs,
      placeholderCoverUrl: selectedEvent.cover_image_url || selectedEvent.file_url || '/fallback-logo.png',
      backgroundMode: derivedMode,
      imageBgColor: color1,
      imageBgColor2: color2,
      bgImageUrl: imageUrl,
      eventTypeOptions,
    };
  }, [selectedEvent]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('user');
    navigate('/');
  };

  return (
    <div className="view-event-container">
      <GlobalHeader
        title="Event Details"
        subtitle="Preview your event request details"
        backTo="/event-request"
        logoutTo="/"
        onLogout={handleLogout}
      />

      <div className="view-event-shell-wrapper">
        <EditableCustomerEventCard
          formData={formData}
          tabs={tabs}
          packages={packages}
          coverImageFile={null}
          activeTab={activeTab}
          onSelectTab={setActiveTab}
          handleInputChange={() => {}}
          handlePackageChange={() => {}}
          handleTabChange={() => {}}
          removePackage={null}
          removeTab={null}
          onImageChange={null}
          eventTypeOptions={eventTypeOptions}
          handleEventTypeChange={() => {}}
          customEventType=""
          handleCustomEventTypeChange={() => {}}
          placeholderCoverUrl={placeholderCoverUrl}
          backgroundMode={backgroundMode}
          setBackgroundMode={() => {}}
          imageBgColor={imageBgColor}
          setImageBgColor={() => {}}
          imageBgColor2={imageBgColor2}
          setImageBgColor2={() => {}}
          bgImageUrl={bgImageUrl}
          setBgImageUrl={() => {}}
        />
      </div>
    </div>
  );
};

export default ViewEventRequest;
